// Intro Teaser — schema v2 (free-position + border), extended additively.
// New optional fields keep old behaviour when absent:
//   textAlign 'left' · textTransform 'none' · startSec 0 · durationSec 0 (= whole segment)

export const TEASER_SCHEMA_VERSION = 2;

/** Fonts the render server has (helper/teaser_backend.py _FONT_CANDIDATES). */
export const TEASER_FONTS = [
  { value: 'Arial Black', css: '"Arial Black", Arial, sans-serif' },
  { value: 'Impact', css: 'Impact, "Arial Black", sans-serif' },
  { value: 'Arial', css: 'Arial, Helvetica, sans-serif' },
  { value: 'Segoe UI', css: '"Segoe UI", Arial, sans-serif' },
  { value: 'Verdana', css: 'Verdana, sans-serif' },
  { value: 'Tahoma', css: 'Tahoma, Verdana, sans-serif' },
  { value: 'Trebuchet MS', css: '"Trebuchet MS", sans-serif' },
  { value: 'Calibri', css: 'Calibri, Arial, sans-serif' },
  { value: 'Georgia', css: 'Georgia, serif' },
  { value: 'Times New Roman', css: '"Times New Roman", Times, serif' },
  { value: 'Courier New', css: '"Courier New", monospace' },
  { value: 'Comic Sans MS', css: '"Comic Sans MS", cursive' },
];

export function teaserFontCss(family) {
  const f = TEASER_FONTS.find((x) => x.value.toLowerCase() === String(family || '').toLowerCase());
  return f ? f.css : `"${family || 'Arial Black'}", Arial, sans-serif`;
}

export const DEFAULT_TEASER_CONFIG = {
  schemaVersion: TEASER_SCHEMA_VERSION,
  chyronText: 'COMING UP...',
  layoutMode: 'free', // 'classic' = legacy bottom full-width | 'free' = draggable box

  // Position (% of canvas, top-left of banner widget)
  xPct: 5,
  yPct: 78,
  widthPct: 90,

  colorMode: 'color',
  accentColor: '#FF1A1A',
  stripColor: '#000000',
  stripOpacity: 0.93,
  textColor: '#FFFFFF',
  contentEffect: 'none',
  contentDesaturate: 0.65,
  contrast: 1.15,
  vignette: false,

  showAccentBar: true,
  showFlagTab: false,
  showBottomLine: false,

  borderEnabled: false,
  borderColor: '#FFFFFF',
  borderWidth: 2,
  borderRadiusPct: 0,

  // Canvas-relative sizing
  chyronHeightPct: 0.12,
  textSizePct: 0.075,
  accentBarHeightPct: 0.008,
  flagWidthPct: 0.018,
  textPadXPct: 0.02,
  bottomLineHeightPct: 0.004,
  padYPct: 0.01,

  fontFamily: 'Arial Black',
  fontWeight: 'bold',
  letterSpacing: 0.06,
  textShadow: true,

  // v2.1 (optional)
  textAlign: 'left',
  textTransform: 'none',
  startSec: 0,
  durationSec: 0,
};

const preset = (id, label, description, overrides) => ({
  id,
  label,
  description,
  config: { ...DEFAULT_TEASER_CONFIG, ...overrides },
});

export const TEASER_PRESETS = [
  preset('minimal', 'Minimal Bar', 'Clean dark bar — drag anywhere', {
    layoutMode: 'free', xPct: 5, yPct: 82, widthPct: 90, stripColor: '#111111', stripOpacity: 0.88,
    showAccentBar: false, showFlagTab: false, chyronHeightPct: 0.1, textSizePct: 0.07,
  }),
  preset('breaking_news', 'Breaking News', 'Classic full-width bottom chyron', {
    layoutMode: 'classic', accentColor: '#FF1A1A', stripColor: '#000000', stripOpacity: 0.94,
    showAccentBar: true, showFlagTab: true, showBottomLine: true, chyronHeightPct: 0.17, textSizePct: 0.09, accentBarHeightPct: 0.014,
  }),
  preset('boxed_accent', 'Boxed Accent', 'Bordered box with red accent', {
    layoutMode: 'free', xPct: 8, yPct: 75, widthPct: 84, accentColor: '#FF1A1A', borderEnabled: true,
    borderColor: '#FF1A1A', borderWidth: 3, showAccentBar: true, showFlagTab: false,
  }),
  preset('documentary', 'Documentary Warm', 'Warm tones, subtle bar', {
    layoutMode: 'free', xPct: 6, yPct: 80, widthPct: 88, accentColor: '#D97706', stripColor: '#1C1410',
    textColor: '#FDE68A', colorMode: 'sepia', contentEffect: 'desaturate', contentDesaturate: 0.55,
    showAccentBar: false, chyronHeightPct: 0.11, textSizePct: 0.068, fontFamily: 'Georgia',
  }),
  preset('large_center', 'Large Center', 'Big centered lower-third', {
    layoutMode: 'free', xPct: 15, yPct: 72, widthPct: 70, chyronHeightPct: 0.14, textSizePct: 0.095,
    stripOpacity: 0.92, borderEnabled: true, borderColor: '#FFFFFF', borderWidth: 2, textAlign: 'center',
  }),
  preset('top_ticker', 'Top Ticker', 'Slim banner across the top', {
    layoutMode: 'free', xPct: 0, yPct: 4, widthPct: 100, chyronHeightPct: 0.085, textSizePct: 0.052,
    stripColor: '#0B1220', stripOpacity: 0.9, accentColor: '#38BDF8', showAccentBar: false, showFlagTab: true,
    flagWidthPct: 0.012, fontFamily: 'Segoe UI', textTransform: 'uppercase', letterSpacing: 0.1,
  }),
  preset('noir', 'Noir', 'Black & white footage, white bar', {
    layoutMode: 'free', xPct: 10, yPct: 76, widthPct: 80, stripColor: '#FFFFFF', stripOpacity: 0.95,
    textColor: '#0A0A0A', accentColor: '#0A0A0A', showAccentBar: true, contentEffect: 'grayscale',
    vignette: true, textShadow: false, textAlign: 'center', fontFamily: 'Georgia', chyronHeightPct: 0.11, textSizePct: 0.07,
  }),
  preset('neon_glow', 'Neon Pop', 'Purple bar, punchy contrast', {
    layoutMode: 'free', xPct: 6, yPct: 78, widthPct: 70, stripColor: '#1E0B3A', stripOpacity: 0.92,
    accentColor: '#A855F7', textColor: '#F5D0FE', showAccentBar: true, showFlagTab: true, contentEffect: 'contrast',
    contrast: 1.25, fontFamily: 'Impact', textSizePct: 0.085, chyronHeightPct: 0.13,
  }),
  preset('custom', 'Custom', 'Your saved edits', {}),
];

export function normalizeTeaserConfig(raw) {
  let source = raw;
  if (source?.config && typeof source.config === 'object') {
    source = { ...source.config, presetId: source.presetId };
  }
  const cfg = { ...DEFAULT_TEASER_CONFIG, ...(source || {}) };
  cfg.schemaVersion = TEASER_SCHEMA_VERSION;
  if (!cfg.layoutMode) {
    cfg.layoutMode = cfg.xPct != null || cfg.widthPct != null ? 'free' : 'classic';
  }
  return cfg;
}

export function buildTeaserPayload(presetId, config) {
  return {
    presetId: presetId || 'custom',
    config: normalizeTeaserConfig(config),
  };
}

export function parseTeaserPayload(payload) {
  if (!payload || typeof payload !== 'object' || !payload.config) return null;
  return {
    presetId: payload.presetId || 'custom',
    config: normalizeTeaserConfig(payload.config),
  };
}

export function getPresetById(id) {
  return TEASER_PRESETS.find((p) => p.id === id) || TEASER_PRESETS[0];
}

/** Same math as helper/teaser_backend.py compute_intro_chyron_geometry. */
export function teaserLayoutMetrics(config, dims) {
  const cfg = normalizeTeaserConfig(config);
  const chyronHeightPct = cfg.chyronHeightPct ?? 0.12;
  const textSizePct = cfg.textSizePct ?? 0.075;
  const accentBarHeightPct = cfg.accentBarHeightPct ?? 0.008;
  const flagWidthPct = cfg.flagWidthPct ?? 0.018;
  const textPadXPct = cfg.textPadXPct ?? 0.02;
  const bottomLineHeightPct = cfg.bottomLineHeightPct ?? 0.004;

  // backend minimums are in 1080p-ish pixels; scale them to the preview
  const k = dims.ref ? dims.h / dims.ref.h : 1;
  const chyronH = Math.max(32 * k, dims.h * chyronHeightPct);
  const textSize = Math.max(14 * k, dims.h * textSizePct);
  const accentH = cfg.showAccentBar !== false ? Math.max(3 * k, dims.h * accentBarHeightPct) : 0;
  const flagW = cfg.showFlagTab !== false ? Math.max(6 * k, dims.w * flagWidthPct) : 0;
  const textPadX = Math.max(8 * k, dims.w * textPadXPct);
  const bottomH = cfg.showBottomLine !== false ? Math.max(2 * k, dims.h * bottomLineHeightPct) : 0;
  const stripH = Math.max(18 * k, chyronH);
  const widgetH = accentH + stripH + bottomH;

  let boxW;
  let boxX;
  let boxY;

  if (cfg.layoutMode === 'classic') {
    boxW = dims.w;
    boxX = 0;
    boxY = dims.h - widgetH;
  } else {
    boxW = Math.max(60 * k, dims.w * ((cfg.widthPct ?? 90) / 100));
    boxX = dims.w * ((cfg.xPct ?? 5) / 100);
    boxY = dims.h * ((cfg.yPct ?? 78) / 100);
    boxX = Math.max(0, Math.min(boxX, dims.w - boxW));
    boxY = Math.max(0, Math.min(boxY, dims.h - widgetH));
  }

  return {
    cfg,
    chyronH,
    textSize,
    accentH,
    flagW,
    textPadX,
    bottomH,
    stripH,
    widgetH,
    boxW,
    boxX,
    boxY,
  };
}
